import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  useConfirmWorkspaceOperationMutation,
  useWorkspaceOperationsQuery,
  type WorkspaceOperation,
  type MissionVersion,
} from './workspaceApi';
import { RuntimeBudgetPanel } from './RuntimeBudgetPanel';
import { MissionRunList } from './MissionRunList';
import { OperationActionReview } from './OperationActionReview';
import { useDesignWorkspace } from '@shared/designVariants/DesignWorkspaceProvider';

export const OperationJournal = ({
  missionId,
  versions,
  writesBlocked = false,
}: {
  missionId?: string;
  versions?: MissionVersion[];
  writesBlocked?: boolean;
}) => {
  const underlyingDirty = useDesignWorkspace().underlyingDirty;
  const writeBlock = useRef(writesBlocked || underlyingDirty);
  writeBlock.current = writesBlocked || underlyingDirty;
  const [review, setReview] = useState<WorkspaceOperation | null>(null);
  const [error, setError] = useState('');
  const [confirm, { isLoading }] = useConfirmWorkspaceOperationMutation();
  const busy = useRef(false);
  const [before, setBefore] = useState<string>();
  const [previous, setPrevious] = useState<WorkspaceOperation[]>([]);
  const query = useWorkspaceOperationsQuery(
    { missionId, before },
    { pollingInterval: before ? 0 : 5000, skipPollingIfUnfocused: true },
  );
  const rows = [...previous, ...(query.currentData?.slice(0, 50) ?? [])];
  useEffect(() => {
    setBefore(undefined);
    setPrevious([]);
  }, [missionId]);
  return (
    <Stack gap={1}>
      <Typography variant="h6">Run journal</Typography>
      {query.isError && (
        <Alert
          severity="error"
          action={<Button onClick={() => void query.refetch()}>Retry</Button>}>
          Unable to load the run journal.
        </Alert>
      )}
      {!rows.length && !query.isFetching && !query.isError && (
        <Typography color="text.secondary">No executions yet.</Typography>
      )}
      {missionId ? (
        <MissionRunList rows={rows} onReview={setReview} versions={versions} />
      ) : (
        rows.map((row) => (
          <Paper key={row.id} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" gap={2} flexWrap="wrap">
              <Chip label={row.state} />
              <Typography>{new Date(row.created_at).toLocaleString()}</Typography>
              <Typography variant="caption">{row.id}</Typography>
            </Stack>
            {row.error_code && (
              <Alert severity={row.state === 'review' ? 'warning' : 'error'}>
                {row.error_code === 'effect_requires_review' ||
                row.error_code === 'execution_interrupted'
                  ? 'Execution outcome requires manual review. Inspect the campaign or delivery before starting another run.'
                  : row.error_code.replaceAll('_', ' ')}
              </Alert>
            )}
            {row.state === 'prepared' && (
              <Button onClick={() => setReview(row)}>Review saved proposal</Button>
            )}
            <Typography variant="body2">
              {row.actions.map((a) => a.kind).join(' → ') ||
                (row.mission_id ? 'No actions executed' : 'Assistant request')}
            </Typography>
            {row.runtimeBudget && <RuntimeBudgetPanel operation={row} />}
            {!!row.result.length && (
              <Box component="pre" sx={{ overflow: 'auto', whiteSpace: 'pre-wrap', fontSize: 12 }}>
                {JSON.stringify(row.result, null, 2)}
              </Box>
            )}
          </Paper>
        ))
      )}
      {(query.currentData?.length ?? 0) > 50 && (
        <Button
          disabled={query.isFetching}
          onClick={() => {
            setPrevious(rows);
            setBefore(rows.at(-1)!.id);
          }}>
          Load older runs
        </Button>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <Dialog
        open={!!review}
        onClose={() => {
          if (!isLoading) setReview(null);
        }}
        fullWidth
        maxWidth="sm">
        <DialogTitle>Review saved proposal</DialogTitle>
        <DialogContent>
          {review && (
            <OperationActionReview actions={review.actions} snapshots={review.snapshots} />
          )}
          {(writesBlocked || underlyingDirty) &&
            review?.actions.some((action) => action.kind !== 'campaign-summary') && (
              <Alert severity="warning">
                Save or discard the current workspace edits before confirming changes.
              </Alert>
            )}
          <Typography>
            Confirmation authorizes these actions with current permissions and target checks.
            Expired proposals must be prepared again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={isLoading} onClick={() => setReview(null)}>
            Close
          </Button>
          {[true, false].map((cancel) => (
            <Button
              key={String(cancel)}
              disabled={
                isLoading ||
                (!cancel &&
                  (writesBlocked || underlyingDirty) &&
                  !!review?.actions.some((action) => action.kind !== 'campaign-summary'))
              }
              onClick={() => {
                if (!review || busy.current) return;
                if (
                  !cancel &&
                  writeBlock.current &&
                  review.actions.some((action) => action.kind !== 'campaign-summary')
                ) {
                  setError(
                    'Save or discard the current workspace edits before confirming changes.',
                  );
                  return;
                }
                busy.current = true;
                void confirm({ id: review.id, cancel })
                  .unwrap()
                  .then(() => {
                    setReview(null);
                    setError('');
                  })
                  .catch(() =>
                    setError(
                      'Unable to apply this decision. Inspect the current proposal and run state before retrying.',
                    ),
                  )
                  .finally(() => {
                    busy.current = false;
                  });
              }}>
              {cancel ? 'Cancel proposal' : 'Confirm and execute'}
            </Button>
          ))}
        </DialogActions>
      </Dialog>
      {before && (
        <Button
          onClick={() => {
            setBefore(undefined);
            setPrevious([]);
          }}>
          Return to latest runs
        </Button>
      )}
    </Stack>
  );
};
